import React, { useState, useEffect } from 'react';
import axios from 'axios';

const About2Modal = ({ isOpen, onClose, initialData }) => {
  const [aboutData, setAboutData] = useState({
    heading: "",
    subheading: "",
    features: [],
    stats: [],
    buttonText: "",
    buttonLink: "",
    colors: {
      primary: "#0760ad",
      primaryLight: "#098a48",
      secondary: "#118a94",
      secondaryLight: "#339166",
      white: "#FFFFFF",
      lightBg: "#f8fafc"
    }
  });

  const [activeTab, setActiveTab] = useState('main');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(null);

  useEffect(() => {
    const fetchCurrentData = async () => {
      if (isOpen && !initialData) {
        try {
          setIsLoading(true);
          const response = await fetch(`${import.meta.env.VITE_API_URL}/about/about2/`);
          if (response.ok) {
            const data = await response.json();
            setAboutData({
              heading: data.heading || "",
              subheading: data.subheading || "",
              features: data.features || [],
              stats: data.stats || [],
              buttonText: data.buttonText || "",
              buttonLink: data.buttonLink || "",
              colors: data.colors || {
                primary: "#0760ad",
                primaryLight: "#098a48",
                secondary: "#118a94",
                secondaryLight: "#339166",
                white: "#FFFFFF",
                lightBg: "#f8fafc"
              }
            });
          }
        } catch (error) {
          console.error('Error fetching about2 data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCurrentData();
  }, [isOpen, initialData]);

  useEffect(() => {
    if (initialData) {
      setAboutData(initialData);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAboutData(prev => ({ ...prev, [name]: value }));
  };

  const handleColorChange = (colorName, value) => {
    setAboutData(prev => ({
      ...prev,
      colors: {
        ...prev.colors,
        [colorName]: value
      }
    }));
  };

  const handleFeatureChange = (index, field, value) => {
    const updatedFeatures = [...aboutData.features];
    updatedFeatures[index] = { ...updatedFeatures[index], [field]: value };
    setAboutData(prev => ({ ...prev, features: updatedFeatures }));
  };

  const handleStatChange = (index, field, value) => {
    const updatedStats = [...aboutData.stats];
    updatedStats[index] = { ...updatedStats[index], [field]: value };
    setAboutData(prev => ({ ...prev, stats: updatedStats }));
  };

  const addFeature = () => {
    setAboutData(prev => ({
      ...prev,
      features: [...prev.features, { 
        title: "",
        description: "",
        icon: "",
        icon_type: "emoji",
        color: prev.colors.primary
      }]
    }));
  };

  const removeFeature = (index) => {
    const updatedFeatures = [...aboutData.features];
    updatedFeatures.splice(index, 1);
    setAboutData(prev => ({ ...prev, features: updatedFeatures }));
  };

  const addStat = () => {
    setAboutData(prev => ({
      ...prev,
      stats: [...prev.stats, { 
        value: "",
        label: "",
        color: prev.colors.primary
      }]
    }));
  };

  const removeStat = (index) => {
    const updatedStats = [...aboutData.stats];
    updatedStats.splice(index, 1);
    setAboutData(prev => ({ ...prev, stats: updatedStats }));
  };

  const handleIconUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('category', 'feature_icons');

    try {
      setIsUploading(true);
      setCurrentUploadIndex(index);
      setUploadProgress(0);

      const response = await axios.post(`${import.meta.env.VITE_API_URL}/images/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      });

      const imageUrl = response.data.image || response.data.image.url;
      handleFeatureChange(index, 'icon', imageUrl);
      handleFeatureChange(index, 'icon_type', 'image');
    } catch (error) {
      console.error('Error uploading icon:', error);
      alert('Failed to upload icon. Please try again.');
    } finally {
      setIsUploading(false);
      setCurrentUploadIndex(null);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/about/about2/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(aboutData)
      });

      if (response.ok) {
        const updatedData = await response.json();
        onClose();
      } else {
        console.error('Failed to update about2 data');
        alert('Failed to save changes. Please try again.');
      }
    } catch (error) {
      console.error('Error updating about2 data:', error);
      alert('An error occurred while saving. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <span className="loading loading-spinner loading-lg"></span>
          <p>Loading about section data...</p>
        </div>
      </dialog>
    );
  }

  return (
    <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
      <div className="modal-box w-11/12 max-w-5xl max-h-[90vh] relative">
        <button onClick={onClose} className="btn btn-sm btn-circle absolute right-2 top-2">✕</button>
        <h3 className="font-bold text-lg mb-4">Edit About Section</h3>
        
        <div className="tabs tabs-boxed mb-6">
          <button 
            className={`tab ${activeTab === 'main' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('main')}
          >
            Main Content
          </button>
          <button 
            className={`tab ${activeTab === 'features' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('features')}
          >
            Features ({aboutData.features.length})
          </button>
          <button 
            className={`tab ${activeTab === 'stats' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            Stats ({aboutData.stats.length})
          </button>
          <button 
            className={`tab ${activeTab === 'design' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('design')}
          >
            Design
          </button>
        </div>
        
        <div className="overflow-y-auto max-h-[60vh] pr-2">
          {activeTab === 'main' && (
            <div className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Heading</span>
                </label>
                <input 
                  type="text" 
                  name="heading" 
                  value={aboutData.heading || ''} 
                  onChange={handleChange} 
                  className="input input-bordered w-full" 
                />
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Subheading</span>
                </label>
                <textarea 
                  name="subheading" 
                  value={aboutData.subheading || ''} 
                  onChange={handleChange} 
                  className="textarea textarea-bordered w-full h-32" 
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Button Text</span>
                  </label>
                  <input 
                    type="text" 
                    name="buttonText" 
                    value={aboutData.buttonText || ''} 
                    onChange={handleChange} 
                    className="input input-bordered w-full" 
                  />
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Button Link</span>
                  </label>
                  <input 
                    type="text" 
                    name="buttonLink" 
                    value={aboutData.buttonLink || ''} 
                    onChange={handleChange} 
                    className="input input-bordered w-full" 
                  />
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'features' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-bold">Features</h4>
                <button 
                  onClick={addFeature}
                  className="btn btn-sm btn-primary"
                >
                  Add Feature
                </button>
              </div>

              {aboutData.features.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No features added yet. Click "Add Feature" to create one.
                </div>
              ) : (
                aboutData.features.map((feature, index) => (
                  <div key={index} className="card bg-base-100 shadow-sm p-4 relative mb-6">
                    <button 
                      onClick={() => removeFeature(index)}
                      className="btn btn-sm btn-circle btn-error absolute -top-2 -right-2 z-10"
                    >
                      ✕
                    </button>
                    
                    <h4 className="font-bold mb-3">Feature {index + 1}</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Title</span>
                        </label>
                        <input 
                          type="text" 
                          value={feature?.title || ''} 
                          onChange={(e) => handleFeatureChange(index, 'title', e.target.value)} 
                          className="input input-bordered w-full" 
                        />
                      </div>
                      
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Icon</span>
                        </label>
                        <div className="flex flex-col gap-2">
                          <select
                            value={feature?.icon_type || 'emoji'}
                            onChange={(e) => handleFeatureChange(index, 'icon_type', e.target.value)}
                            className="select select-bordered w-full"
                          >
                            <option value="emoji">Emoji</option>
                            <option value="image">Image</option>
                          </select>
                          
                          {feature?.icon_type === 'image' ? (
                            <div className="flex items-center gap-2">
                              {feature?.icon && (
                                <div className="avatar">
                                  <div className="w-12 rounded">
                                    <img 
                                      src={feature.icon} 
                                      alt="Feature icon" 
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = 'https://via.placeholder.com/48?text=Icon';
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                              <input 
                                type="file" 
                                accept="image/*"
                                onChange={(e) => handleIconUpload(e, index)}
                                className="file-input file-input-bordered file-input-sm w-full"
                                disabled={isUploading && currentUploadIndex === index}
                              />
                              {isUploading && currentUploadIndex === index && (
                                <progress 
                                  className="progress progress-primary w-20" 
                                  value={uploadProgress} 
                                  max="100"
                                ></progress>
                              )}
                            </div>
                          ) : (
                            <input 
                              type="text" 
                              value={feature?.icon || ''} 
                              onChange={(e) => handleFeatureChange(index, 'icon', e.target.value)} 
                              className="input input-bordered w-full" 
                              maxLength="2"
                              placeholder="😊"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="form-control mb-4">
                      <label className="label">
                        <span className="label-text">Description</span>
                      </label>
                      <textarea 
                        value={feature?.description || ''} 
                        onChange={(e) => handleFeatureChange(index, 'description', e.target.value)} 
                        className="textarea textarea-bordered w-full h-24" 
                      />
                    </div>
                    
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Color</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input 
                          type="color" 
                          value={feature?.color || aboutData.colors.primary} 
                          onChange={(e) => handleFeatureChange(index, 'color', e.target.value)} 
                          className="h-10 w-10 p-1 rounded border"
                        />
                        <input 
                          type="text" 
                          value={feature?.color || ''} 
                          onChange={(e) => handleFeatureChange(index, 'color', e.target.value)} 
                          className="input input-bordered w-full" 
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          
          {activeTab === 'stats' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-bold">Statistics</h4>
                <button 
                  onClick={addStat}
                  className="btn btn-sm btn-primary"
                >
                  Add Stat
                </button>
              </div>

              {aboutData.stats.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No statistics added yet. Click "Add Stat" to create one.
                </div>
              ) : (
                aboutData.stats.map((stat, index) => (
                  <div key={index} className="card bg-base-100 shadow-sm p-4 relative mb-4">
                    <button 
                      onClick={() => removeStat(index)}
                      className="btn btn-sm btn-circle btn-error absolute -top-2 -right-2 z-10"
                    >
                      ✕
                    </button>
                    
                    <h4 className="font-bold mb-3">Stat {index + 1}</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Value</span>
                        </label>
                        <input 
                          type="text" 
                          value={stat?.value || ''} 
                          onChange={(e) => handleStatChange(index, 'value', e.target.value)} 
                          className="input input-bordered w-full" 
                        />
                      </div>
                      
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Label</span>
                        </label>
                        <input 
                          type="text" 
                          value={stat?.label || ''} 
                          onChange={(e) => handleStatChange(index, 'label', e.target.value)} 
                          className="input input-bordered w-full" 
                        />
                      </div>
                      
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Color</span>
                        </label>
                        <div className="flex items-center gap-2">
                          <input 
                            type="color" 
                            value={stat?.color || aboutData.colors.primary} 
                            onChange={(e) => handleStatChange(index, 'color', e.target.value)} 
                            className="h-10 w-10 p-1 rounded border"
                          />
                          <input 
                            type="text" 
                            value={stat?.color || ''} 
                            onChange={(e) => handleStatChange(index, 'color', e.target.value)} 
                            className="input input-bordered w-full" 
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          
          {activeTab === 'design' && (
            <div className="space-y-4">
              <h4 className="font-bold mb-4">Color Scheme</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Primary Color</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input 
                      type="color" 
                      value={aboutData.colors.primary} 
                      onChange={(e) => handleColorChange('primary', e.target.value)} 
                      className="h-10 w-10 p-1 rounded border"
                    />
                    <input 
                      type="text" 
                      value={aboutData.colors.primary} 
                      onChange={(e) => handleColorChange('primary', e.target.value)} 
                      className="input input-bordered w-full" 
                    />
                  </div>
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Primary Light</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input 
                      type="color" 
                      value={aboutData.colors.primaryLight} 
                      onChange={(e) => handleColorChange('primaryLight', e.target.value)} 
                      className="h-10 w-10 p-1 rounded border"
                    />
                    <input 
                      type="text" 
                      value={aboutData.colors.primaryLight} 
                      onChange={(e) => handleColorChange('primaryLight', e.target.value)} 
                      className="input input-bordered w-full" 
                    />
                  </div>
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Secondary Color</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input 
                      type="color" 
                      value={aboutData.colors.secondary} 
                      onChange={(e) => handleColorChange('secondary', e.target.value)} 
                      className="h-10 w-10 p-1 rounded border"
                    />
                    <input 
                      type="text" 
                      value={aboutData.colors.secondary} 
                      onChange={(e) => handleColorChange('secondary', e.target.value)} 
                      className="input input-bordered w-full" 
                    />
                  </div>
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Secondary Light</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input 
                      type="color" 
                      value={aboutData.colors.secondaryLight} 
                      onChange={(e) => handleColorChange('secondaryLight', e.target.value)} 
                      className="h-10 w-10 p-1 rounded border"
                    />
                    <input 
                      type="text" 
                      value={aboutData.colors.secondaryLight} 
                      onChange={(e) => handleColorChange('secondaryLight', e.target.value)} 
                      className="input input-bordered w-full" 
                    />
                  </div>
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Background Color</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input 
                      type="color" 
                      value={aboutData.colors.lightBg} 
                      onChange={(e) => handleColorChange('lightBg', e.target.value)} 
                      className="h-10 w-10 p-1 rounded border"
                    />
                    <input 
                      type="text" 
                      value={aboutData.colors.lightBg} 
                      onChange={(e) => handleColorChange('lightBg', e.target.value)} 
                      className="input input-bordered w-full" 
                    />
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 rounded-lg" style={{
                background: aboutData.colors.lightBg,
                height: '100px',
                border: '1px solid #eee'
              }}>
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <p className="font-medium" style={{ color: aboutData.colors.primary }}>Preview</p>
                    <div className="w-24 h-1 mx-auto my-2 rounded-full" style={{ 
                      backgroundColor: aboutData.colors.secondary 
                    }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="modal-action mt-4">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            Save Changes
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default About2Modal;